import isHotkey from 'is-hotkey';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { createEditor, Editor, Node, Range, Element as SlateElement, Transforms } from 'slate';
import { withHistory } from 'slate-history';
import { Editable, Slate, withReact } from 'slate-react';
import useMobileMediaQuery from '../../hooks/useMobileMediaQuery';
import { emptyTemplate } from '../../redux/trip/trip.slice';
import findUrlsInText from '../../utils/findUrlsInText';
import { ChecklistItemElement } from './Checklist';
import { HighlightColorButton, TextColorButton } from './ColorPicker';
import { HOTKEYS } from './constants';
import {
  ALL_LIST_TYPES,
  getCurrentListItem,
  getElementStyle,
  isInListBlock,
  isListItemEmpty,
  toggleMark,
} from './editor.utils';
import {
  getActiveLink,
  insertLink,
  isLinkActive,
  LinkButton,
  LinkElement,
  LinkLeaf,
  LinkModal,
  LinkTooltip,
  updateLink,
  wrapLink,
} from './Link';
import { withLinks, withShortcuts } from './plugins';
import {
  handleBackspaceInTable,
  handleEnterInTable,
  isInTable,
  moveToNextCell,
  moveToPreviousCell,
  TableButton,
  TableCellElement,
  TableElement,
  TableRowElement,
} from './Table';
import { BlockButton, HistoryButton, MarkButton } from './Toolbar';
import './RichTextEditor.scss';

const ELEMENT_TYPE_MAP = {
  'block-quote': 'blockquote',
  'bulleted-list': 'ul',
  'dashed-list': 'ul',
  'heading-one': 'h1',
  'heading-two': 'h2',
  'list-item': 'li',
  'numbered-list': 'ol',
  checklist: 'div',
  'checklist-item': ChecklistItemElement,
  table: TableElement,
  'table-row': TableRowElement,
  'table-cell': TableCellElement,
};

// ============================================================================
// Keyboard Handlers
// ============================================================================

const handleEnterInList = (editor) => {
  const listItem = getCurrentListItem(editor);
  if (!listItem) {
    return false;
  }

  const [node] = listItem;

  if (isListItemEmpty(node)) {
    Transforms.unwrapNodes(editor, {
      match: (n) =>
        !Editor.isEditor(n) && Editor.isBlock(editor, n) && ALL_LIST_TYPES.includes(n.type),
      split: true,
    });
    Transforms.setNodes(editor, { type: 'paragraph' });
    return true;
  }

  const newItem =
    node.type === 'checklist-item'
      ? { type: 'checklist-item', checked: false, children: [{ text: '' }] }
      : { type: 'list-item', children: [{ text: '' }] };

  Transforms.insertNodes(editor, newItem);
  return true;
};

const handleBackspaceInList = (editor) => {
  const { selection } = editor;
  if (!selection || !Range.isCollapsed(selection) || selection.anchor.offset !== 0) {
    return false;
  }

  const listItem = getCurrentListItem(editor);
  if (!listItem) {
    return false;
  }

  const [node] = listItem;

  if (isListItemEmpty(node)) {
    Transforms.unwrapNodes(editor, {
      match: (n) =>
        !Editor.isEditor(n) && Editor.isBlock(editor, n) && ALL_LIST_TYPES.includes(n.type),
      split: true,
    });
    Transforms.setNodes(editor, { type: 'paragraph' });
    return true;
  }

  return false;
};

const handleIndent = (editor) => {
  const listItem = getCurrentListItem(editor);
  if (!listItem) {
    Transforms.insertText(editor, '  ');
    return;
  }

  const [parentList] = Editor.nodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) && Editor.isBlock(editor, n) && ALL_LIST_TYPES.includes(n.type),
  });

  if (parentList) {
    const [parentNode] = parentList;
    Transforms.wrapNodes(editor, { type: parentNode.type, children: [] });
  }
};

const handleOutdent = (editor) => {
  const listItem = getCurrentListItem(editor);
  if (!listItem) {
    return;
  }

  const [, listItemPath] = listItem;

  const parentListEntry = Editor.above(editor, {
    at: listItemPath,
    match: (n) =>
      !Editor.isEditor(n) && Editor.isBlock(editor, n) && ALL_LIST_TYPES.includes(n.type),
  });

  if (!parentListEntry) {
    return;
  }

  const [, parentListPath] = parentListEntry;

  const grandparentListEntry = Editor.above(editor, {
    at: parentListPath,
    match: (n) =>
      !Editor.isEditor(n) && Editor.isBlock(editor, n) && ALL_LIST_TYPES.includes(n.type),
  });

  if (grandparentListEntry) {
    Transforms.liftNodes(editor, { at: listItemPath });
  } else {
    Transforms.unwrapNodes(editor, {
      match: (n) =>
        !Editor.isEditor(n) && Editor.isBlock(editor, n) && ALL_LIST_TYPES.includes(n.type),
      split: true,
    });
    Transforms.setNodes(editor, { type: 'paragraph' });
  }
};

// ============================================================================
// Render Components
// ============================================================================

const Element = ({ attributes, children, element, readOnly, onLinkHover, onLinkLeave, editor }) => {
  const style = getElementStyle(element);

  if (element.type === 'link') {
    return (
      <LinkElement
        attributes={attributes}
        element={element}
        onHover={onLinkHover}
        onLeave={onLinkLeave}
        editor={editor}
      >
        {children}
      </LinkElement>
    );
  }

  const Component = ELEMENT_TYPE_MAP[element.type] || 'p';

  return (
    <Component
      style={style}
      {...attributes}
      element={element}
      readOnly={readOnly}
    >
      {children}
    </Component>
  );
};

const Leaf = ({ attributes, children, leaf }) => {
  let content = children;

  if (leaf.bold) {
    content = <strong>{content}</strong>;
  }
  if (leaf.italic) {
    content = <em>{content}</em>;
  }
  if (leaf.underline) {
    content = <u>{content}</u>;
  }
  if (leaf.strikethrough) {
    content = <s>{content}</s>;
  }
  if (leaf.decoration === 'link') {
    content = <LinkLeaf leaf={leaf}>{content}</LinkLeaf>;
  }

  const style = {};
  if (leaf.color) {
    style.color = leaf.color;
  }
  if (leaf.highlight) {
    style.backgroundColor = leaf.highlight;
  }

  return (
    <span
      {...attributes}
      style={style}
    >
      {content}
    </span>
  );
};

// ============================================================================
// Link Decorator
// ============================================================================

const LinkDecorator = ([node, path]) => {
  const nodeText = node.text;

  if (!nodeText) {
    return [];
  }

  return findUrlsInText(nodeText).flatMap(([url, index]) => ({
    anchor: { path, offset: index },
    focus: { path, offset: index + url.length },
    decoration: 'link',
  }));
};

// ============================================================================
// Main Component
// ============================================================================

const INITIAL_LINK_MODAL_DATA = {
  url: '',
  text: '',
  isEditing: false,
  linkPath: null,
};

const RichTextEditor = ({ initialValue, onChange, toolbar, placeholder, readOnly }) => {
  const _isMobile = useMobileMediaQuery();

  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkModalData, setLinkModalData] = useState(INITIAL_LINK_MODAL_DATA);

  const [hoveredLink, setHoveredLink] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState(null);
  const tooltipTimeoutRef = useRef(null);

  const savedSelectionRef = useRef(null);

  const editor = useMemo(
    () => withLinks(withShortcuts(withReact(withHistory(createEditor())))),
    []
  );

  const handleLinkHover = useCallback((element, position) => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }
    setHoveredLink(element);
    setTooltipPosition(position);
  }, []);

  const handleLinkLeave = useCallback(() => {
    tooltipTimeoutRef.current = setTimeout(() => {
      setHoveredLink(null);
      setTooltipPosition(null);
    }, 300);
  }, []);

  const openLinkModal = useCallback(
    (url = '', text = '', isEditing = false, linkPath = null) => {
      if (isEditing && linkPath) {
        savedSelectionRef.current = null;
      } else if (editor.selection) {
        savedSelectionRef.current = { ...editor.selection };

        if (!text && !Range.isCollapsed(editor.selection)) {
          text = Editor.string(editor, editor.selection);
        }
      } else {
        savedSelectionRef.current = null;
      }

      Transforms.deselect(editor);

      setLinkModalData({ url, text, isEditing, linkPath });
      setLinkModalOpen(true);
    },
    [editor]
  );

  const _closeLinkModal = useCallback(() => {
    setLinkModalOpen(false);
    setLinkModalData(INITIAL_LINK_MODAL_DATA);
    savedSelectionRef.current = null;
  }, []);

  const handleLinkSubmit = useCallback(
    (url, text) => {
      if (linkModalData.isEditing && linkModalData.linkPath) {
        try {
          updateLink(editor, url, linkModalData.linkPath, text);
        } catch {
          // Path may no longer be valid
        }
      } else {
        Editor.withoutNormalizing(editor, () => {
          try {
            if (savedSelectionRef.current) {
              Transforms.select(editor, savedSelectionRef.current);

              if (!Range.isCollapsed(savedSelectionRef.current)) {
                wrapLink(editor, url);
              } else {
                insertLink(editor, url, text || url);
              }
            } else {
              Transforms.select(editor, Editor.end(editor, []));
              insertLink(editor, url, text || url);
            }
          } catch {
            try {
              Transforms.select(editor, Editor.end(editor, []));
              insertLink(editor, url, text || url);
            } catch {
              // Give up
            }
          }
        });
      }

      savedSelectionRef.current = null;
      setLinkModalOpen(false);
      setLinkModalData(INITIAL_LINK_MODAL_DATA);

      setTimeout(() => {
        try {
          Transforms.deselect(editor);
        } catch {
          // Ignore
        }
      }, 0);
    },
    [editor, linkModalData]
  );

  const handleLinkCancel = useCallback(() => {
    savedSelectionRef.current = null;
    setLinkModalOpen(false);
    setLinkModalData(INITIAL_LINK_MODAL_DATA);

    setTimeout(() => {
      try {
        Transforms.deselect(editor);
      } catch {
        // Ignore
      }
    }, 0);
  }, [editor]);

  const handleEditLink = useCallback(() => {
    if (hoveredLink && hoveredLink.path) {
      const text = Node.string(hoveredLink.element);
      openLinkModal(hoveredLink.element.url, text, true, hoveredLink.path);
    }
    setHoveredLink(null);
    setTooltipPosition(null);
  }, [hoveredLink, openLinkModal]);

  const handleRemoveLink = useCallback(() => {
    if (hoveredLink && hoveredLink.path) {
      try {
        Transforms.unwrapNodes(editor, {
          at: hoveredLink.path,
          match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'link',
        });
      } catch {
        // Path may no longer be valid
      }
    }
    setHoveredLink(null);
    setTooltipPosition(null);
  }, [editor, hoveredLink]);

  const renderElement = useCallback(
    (props) => (
      <Element
        {...props}
        readOnly={readOnly}
        onLinkHover={handleLinkHover}
        onLinkLeave={handleLinkLeave}
        editor={editor}
      />
    ),
    [readOnly, handleLinkHover, handleLinkLeave, editor]
  );

  const renderLeaf = useCallback((props) => <Leaf {...props} />, []);

  const handleOnChange = useCallback(
    (value) => {
      const isAstChange = editor.operations.some((op) => op.type !== 'set_selection');
      if (isAstChange) {
        onChange(value);
      }
    },
    [editor.operations, onChange]
  );

  const onKeyDown = useCallback(
    (event) => {
      event.stopPropagation(); // Prevent dnd-kit listeners on parent from intercepting
      for (const hotkey in HOTKEYS) {
        if (isHotkey(hotkey, event)) {
          event.preventDefault();
          toggleMark(editor, HOTKEYS[hotkey]);
          return;
        }
      }

      if (isHotkey('mod+k', event)) {
        event.preventDefault();
        if (isLinkActive(editor)) {
          const linkEntry = getActiveLink(editor);
          if (linkEntry) {
            const [node, path] = linkEntry;
            const text = Node.string(node);
            openLinkModal(node.url, text, true, path);
          }
        } else {
          openLinkModal();
        }
        return;
      }

      if (isHotkey('tab', event)) {
        event.preventDefault();
        if (isInTable(editor)) {
          moveToNextCell(editor);
          return;
        }
        handleIndent(editor);
        return;
      }

      if (isHotkey('shift+tab', event)) {
        event.preventDefault();
        if (isInTable(editor)) {
          moveToPreviousCell(editor);
          return;
        }
        handleOutdent(editor);
        return;
      }

      if (isHotkey('enter', event) && isInTable(editor) && handleEnterInTable(editor)) {
        event.preventDefault();
        return;
      }

      if (isHotkey('enter', event) && isInListBlock(editor)) {
        if (handleEnterInList(editor)) {
          event.preventDefault();
        }
        return;
      }

      if (isHotkey('backspace', event) && isInTable(editor) && handleBackspaceInTable(editor)) {
        event.preventDefault();
        return;
      }

      if (isHotkey('backspace', event) && handleBackspaceInList(editor)) {
        event.preventDefault();
        return;
      }

      if (isHotkey('shift+enter', event)) {
        event.preventDefault();
        editor.insertText('\n');
      }
    },
    [editor, openLinkModal]
  );

  return (
    <>
      <Slate
        editor={editor}
        value={initialValue || emptyTemplate}
        onChange={handleOnChange}
      >
        {toolbar && (
          <div className='editor-toolbar'>
            <HistoryButton
              type='undo'
              icon='undo'
            />
            <HistoryButton
              type='redo'
              icon='redo'
            />
            <span className='toolbar-divider' />
            <MarkButton
              format='bold'
              icon='format_bold'
            />
            <MarkButton
              format='italic'
              icon='format_italic'
            />
            <MarkButton
              format='underline'
              icon='format_underlined'
            />
            <MarkButton
              format='strikethrough'
              icon='format_strikethrough'
            />
            <TextColorButton />
            <HighlightColorButton />
            <span className='toolbar-divider' />
            <LinkButton onOpenModal={openLinkModal} />
            <span className='toolbar-divider' />
            <BlockButton
              format='heading-one'
              icon='looks_one'
            />
            <BlockButton
              format='heading-two'
              icon='looks_two'
            />
            <BlockButton
              format='block-quote'
              icon='format_quote'
            />
            <span className='toolbar-divider' />
            <BlockButton
              format='numbered-list'
              icon='format_list_numbered'
            />
            <BlockButton
              format='bulleted-list'
              icon='format_list_bulleted'
            />
            <BlockButton
              format='dashed-list'
              icon='remove'
            />
            <BlockButton
              format='checklist'
              icon='checklist'
            />
            <span className='toolbar-divider' />
            <BlockButton
              format='left'
              icon='format_align_left'
            />
            <BlockButton
              format='center'
              icon='format_align_center'
            />
            <BlockButton
              format='right'
              icon='format_align_right'
            />
            <BlockButton
              format='justify'
              icon='format_align_justify'
            />
            <span className='toolbar-divider' />
            <TableButton />
          </div>
        )}
        <Editable
          className='styled-editable'
          renderElement={renderElement}
          renderLeaf={renderLeaf}
          decorate={LinkDecorator}
          placeholder={placeholder}
          spellCheck
          readOnly={readOnly}
          onKeyDown={onKeyDown}
        />
      </Slate>

      <LinkModal
        isOpen={linkModalOpen}
        onClose={handleLinkCancel}
        onSubmit={handleLinkSubmit}
        initialUrl={linkModalData.url}
        initialText={linkModalData.text}
        isEditing={linkModalData.isEditing}
      />

      <LinkTooltip
        link={hoveredLink}
        position={tooltipPosition}
        onEdit={handleEditLink}
        onRemove={handleRemoveLink}
        onMouseEnter={() => {
          if (tooltipTimeoutRef.current) {
            clearTimeout(tooltipTimeoutRef.current);
          }
        }}
        onMouseLeave={() => {
          setHoveredLink(null);
          setTooltipPosition(null);
        }}
        onClose={() => {
          setHoveredLink(null);
          setTooltipPosition(null);
        }}
      />
    </>
  );
};

Element.displayName = 'Element';
Leaf.displayName = 'Leaf';
RichTextEditor.displayName = 'RichTextEditor';

export default RichTextEditor;
